import { FilterMatchMode } from "primereact/api"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { InputText } from "primereact/inputtext"
import React, { useEffect, useState } from "react"
import AddDialogBoxWithDataTable from "../addDialogBoxWithDataTable"
import { useParams } from "react-router-dom"
import {
	assignRoleToGroupService,
	unassignRoleFromGroupService,
} from "services/groupService"
import { ToastContent, toast } from "react-toastify"
import { AxiosError } from "axios"
import DynamicModal from "components/DynamicModal"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"

export type APIAssignedRolesResponse = {
	role_id: number
	name: string
}[]

type IAssignRoleDropdownProps = {
	assigned_roles: APIAssignedRolesResponse
	unassigned_roles: APIAssignedRolesResponse
	fetchGroupData: (id: string | undefined) => Promise<void>
}

const AssignRoleDropdown = ({
	assigned_roles,
	unassigned_roles,
	fetchGroupData,
}: IAssignRoleDropdownProps) => {
	const [filters, setFilters] = useState<{ [s: string]: any }>()
	const [globalFilterValue, setGlobalFilterValue] = useState("")

	const [addModalVisibility, setAddModalVisibility] = useState(false)
	const [first, setFirst] = useState(0); // State to control the first page
	const [sortField, setSortField] = useState(""); // State to control the first page
	const [sortOrder, setSortOrder] = useState<any>(""); // State to control the first page
	const [rowsPerPage, setRowsPerPage] = useState(5); // State to control the rows per page
	const params = useParams()

	const [selectedRolesToUnassign, setSelectedRolesToUnassign] =
		useState<Record<string, any>>()

	const initFilters = () => {
		setFilters({
			global: { value: null, matchMode: FilterMatchMode.CONTAINS },
		})
		setGlobalFilterValue("")
	}

	const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFirst(0);
		const value = e?.target?.value
		let _filters = { ...filters }

		_filters["global"]!.value = value

		setFilters(_filters)
		setGlobalFilterValue(value)
	}
	const handleSort = (event: any) => {
		setSortField(event.sortField)
		setSortOrder(event.sortOrder)
		setFirst(0);  //Move to page 1
		// dispatch(setFirstVoice(0)); // Move to page 1
	};
	useEffect(() => {
		fetchGroupData(params.id)
		initFilters()
	}, [fetchGroupData, params.id])

	const unassignUserHandler = async (id: string) => {
		try {
			const { response, error } = await unassignRoleFromGroupService({
				group_id: Number(params.id),
				role_id: Number(id),
			})

			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message, {
					style: {
						backgroundColor: '#F1F1F1', // Grey background
					}
				})
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const unassignMultipleUserHandler = async (
		roles: Record<string, any> | undefined
	) => {
		try {
			const { response, error } = await unassignRoleFromGroupService({
				group_id: Number(params.id),
				role_id: roles?.map((body: { role_id: any }) => body.role_id),
			})

			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message, {
					style: {
						backgroundColor: '#F1F1F1', // Grey background
					}
				})
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const assignRoleToGroup = async (roles: Record<string, any>) => {
		try {
			const { response, error } = await assignRoleToGroupService({
				group_id: Number(params.id),
				role_id: roles.map(({ role_id }: { role_id: string; name: string }) => {
					return Number(role_id)
				}),
			})
			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
			setAddModalVisibility(false)
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message, {
					style: {
						backgroundColor: '#F1F1F1', // Grey background
					}
				})
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const handleClickClear = () => {
		setSelectedRolesToUnassign([])
	}

	if (!params.id) return <div></div>
	return (
		<div className="user-management-section">
			<div className='d-flex align-items-center flex-wrap justify-content-end mb-3'>
				<div className="d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center dialog-filter">
						<div className="d-flex align-items-center mr-3">
							<IconField iconPosition="right" className='user-input'>
								<InputIcon className="pi pi-search"></InputIcon>
								<InputText
									value={globalFilterValue}
									onChange={onGlobalFilterChange}
									placeholder="Search"
								/>
							</IconField>
						</div>
						<Button
							className="outline-icon-btn mr-3"
							type="button"
							onClick={() => setAddModalVisibility(true)}
						>
							<i className="fa-solid fa-person-circle-plus mr-2"></i> Assign Roles
						</Button>
						<AddDialogBoxWithDataTable
							name="Roles"
							header={"Add roles to the group"}
							onSave={(selectedRolesToAdd) => {
								assignRoleToGroup(selectedRolesToAdd)
							}}
							onCancel={() => setAddModalVisibility(false)}
							addModalVisibility={addModalVisibility}
							data={unassigned_roles}
						/>

						<Button
							className="outline-icon-btn"
							type="button"
							disabled={!selectedRolesToUnassign?.length}
							data-bs-toggle="modal" data-bs-target="#unAssigRolesModal"
						>
							<i className="fa-solid fa-person-circle-xmark mr-2"></i> Unassign Roles
						</Button>
					</div>
				</div>
			</div>
			<DynamicModal text={"Are you sure you want to unassign roles ?"} modalKey={"unAssigRolesModal"} functionToCall={unassignMultipleUserHandler} funcParams={selectedRolesToUnassign} setSelectedRolesToUnassign={setSelectedRolesToUnassign} handleCloseModal={handleClickClear} />
			<div className="datatable-wrapper">
				<DataTable
					selection={selectedRolesToUnassign}
					onSelectionChange={(e) => setSelectedRolesToUnassign(e.value)}
					selectionPageOnly
					paginator
					filters={filters}
					globalFilterFields={["name"]}
					first={first}
					onPage={(e) => { setFirst(e.first); setRowsPerPage(e.rows) }}
					rows={rowsPerPage}
					sortField={sortField}
					sortOrder={sortOrder}
					sortMode="single"
					onSort={handleSort}
					rowsPerPageOptions={[5, 10, 25]}
					stripedRows
					value={assigned_roles}
					emptyMessage="No role records found"
				>
					<Column
						selectionMode="multiple"
						headerStyle={{ width: "3rem" }}
					></Column>
					<Column field="name" header="Role Name" sortable></Column>
					<Column
						header="Action"
						body={ActionButton(unassignUserHandler, handleClickClear)}
					></Column>
				</DataTable>
			</div>
		</div>
	)
}

export default AssignRoleDropdown

function ActionButton(unassignUserHandler: (id: string) => {}, handleClickClear: any) {
	const [unAssignID, setUnAssignID] = useState("")
	return (rowData: { [s: string]: string }) => {
		return (
			<>
				<span
					className="status-tag delete"
					onClick={(e) => { e.stopPropagation(); setUnAssignID(rowData.role_id) }}
					// onClick={() => unassignUserHandler(rowData.role_id)}
					data-bs-toggle="modal" data-bs-target="#unAssigRoleModal"
				>
					Unassign
				</span>
				<DynamicModal text={"Are you sure you want to unassign role ?"} modalKey={"unAssigRoleModal"} functionToCall={unassignUserHandler} funcParams={unAssignID} setSelectedRolesToUnassign={"single"} handleCloseModal={handleClickClear} />
			</>
		)
	}
}
